import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://10.0.0.27:8181";
console.log(API_URL);

export interface Category {
  _id: string;
  name: string;
  description?: string;
  image?: string;
  order: number;
  isActive: boolean;
  isParent: boolean;
  parentId?: string | null;
  children?: Category[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCategoryData {
  name: string;
  description?: string;
  image?: File;
  order?: number;
}

export interface UpdateCategoryData {
  name?: string;
  description?: string;
  image?: File;
  order?: number;
  isActive?: boolean;
}

class CategoryApi {
  // Get all active categories (public) - with children
  async getCategories(): Promise<Category[]> {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      return response.data;
    } catch (error: any) {
      console.error("CategoryApi: Error fetching categories:", error);
      throw error;
    }
  }

  // Get all child categories (for product creation dropdown)
  async getChildCategories(): Promise<Category[]> {
    try {
      const response = await axios.get(`${API_URL}/api/categories/children`);
      return response.data;
    } catch (error: any) {
      console.error("CategoryApi: Error fetching child categories:", error);
      throw error;
    }
  }

  // Get all categories (admin only) - with children
  async getAllCategories(): Promise<Category[]> {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/api/categories/admin`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching all categories:", error);
      throw error;
    }
  }

  // Create new category (admin only)
  async createCategory(categoryData: CreateCategoryData): Promise<Category> {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();

      formData.append("name", categoryData.name);
      if (categoryData.description) {
        formData.append("description", categoryData.description);
      }
      if (categoryData.order !== undefined) {
        formData.append("order", categoryData.order.toString());
      }

      if (categoryData.image) {
        formData.append("image", categoryData.image);
      }

      const response = await axios.post(
        `${API_URL}/api/categories/admin`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating category:", error);
      throw error;
    }
  }

  // Create child category (admin only)
  async createChildCategory(
    parentId: string,
    categoryData: CreateCategoryData
  ): Promise<Category> {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();

      formData.append("name", categoryData.name);
      if (categoryData.description) {
        formData.append("description", categoryData.description);
      }
      if (categoryData.order !== undefined) {
        formData.append("order", categoryData.order.toString());
      }

      if (categoryData.image) {
        formData.append("image", categoryData.image);
      }

      const response = await axios.post(
        `${API_URL}/api/categories/admin/${parentId}/children`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error creating child category:", error);
      throw error;
    }
  }

  // Update category (admin only)
  async updateCategory(
    id: string,
    categoryData: UpdateCategoryData
  ): Promise<Category> {
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();

      if (categoryData.name) {
        formData.append("name", categoryData.name);
      }
      if (categoryData.description !== undefined) {
        formData.append("description", categoryData.description);
      }
      if (categoryData.order !== undefined) {
        formData.append("order", categoryData.order.toString());
      }
      if (categoryData.isActive !== undefined) {
        formData.append("isActive", categoryData.isActive.toString());
      }

      if (categoryData.image) {
        formData.append("image", categoryData.image);
      }

      const response = await axios.put(
        `${API_URL}/api/categories/admin/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error updating category:", error);
      throw error;
    }
  }

  // Delete category (admin only)
  async deleteCategory(id: string): Promise<void> {
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/categories/admin/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      console.error("Error deleting category:", error);
      throw error;
    }
  }

  // Get category by ID (admin only)
  async getCategoryById(id: string): Promise<Category> {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/api/categories/admin/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching category:", error);
      throw error;
    }
  }
}

const categoryApi = new CategoryApi();
export default categoryApi;
